import type { AnalysisResponseDto } from '@inference.engine/api-client/web';

import type { AudienceDraftEnvelope } from '@/features/analysis/api/analysis.api';

export type AudienceDraftStatus = 'pending' | 'processing' | 'ready' | 'failed';

/** The persona an audience was built into, as much of it as a card needs. */
export interface AudienceDraftPersona {
  personaTitle?: string;
  personaBrief?: string;
  segmentName?: string;
  photoAssetId?: string;
  demographicsData?: {
    data?: { firstName?: string; age?: string; profession?: string };
    characteristics?: string[];
  };
}

export interface AudienceDraft {
  draftId: string;
  sourceIndex: number;
  status: AudienceDraftStatus;
  error?: string;
  persona?: AudienceDraftPersona;
  savedPersonaId?: string;
}

export interface AudienceDrafts {
  status: AudienceDraftStatus;
  startedAt?: string;
  items: AudienceDraft[];
}

/** What the report says about an audience before any persona is built from it. */
export interface AudienceSegment {
  name?: string;
  title?: string;
  age?: string;
  description?: string;
}

export interface AudienceDraftCard {
  draftId: string;
  /** Which block the draft lives in, which is what decides the route that acts on it. */
  envelope: AudienceDraftEnvelope;
  status: AudienceDraftStatus;
  saved: boolean;
  segment: AudienceSegment;
  persona?: AudienceDraftPersona;
  /** The persona the save created, which is the only one there is to chat with. */
  savedPersonaId?: string;
}

/**
 * Reads a report's audience drafts, whichever of the two blocks it carries. The generated
 * client types `structuredData` loosely, so this narrows it in one place rather than at each
 * use — the same shape `audience` is already read with.
 *
 * A report holds one block or the other, never both: audiences it was given as documents are
 * stamped first, and stamping any of them is what stops the suggested ones being stamped at
 * all. Reading the uploaded block first keeps that order, so a report whose research renamed
 * some of its documents' audiences still shows the suggested fallback it was given instead.
 *
 * A report with neither predates the feature, and says so by returning `undefined` rather
 * than an empty list, so callers can tell "none to build" from "not part of this".
 */
export const readAudienceDrafts = (
  analysis: AnalysisResponseDto,
): { envelope: AudienceDraftEnvelope; drafts: AudienceDrafts } | undefined => {
  const structured = analysis.structuredData as
    | { uploadedAudiences?: AudienceDrafts; suggestedAudiences?: AudienceDrafts }
    | undefined;

  const uploaded = structured?.uploadedAudiences;
  if (uploaded?.items) {
    return { envelope: 'uploadedAudiences', drafts: uploaded };
  }

  const suggested = structured?.suggestedAudiences;
  if (suggested?.items) {
    return { envelope: 'suggestedAudiences', drafts: suggested };
  }

  return undefined;
};

/**
 * Pairs each audience draft with the research it came from, so a card can name the audience
 * while its persona is still being built.
 */
export const toAudienceDraftCards = (analysis: AnalysisResponseDto): AudienceDraftCard[] => {
  const read = readAudienceDrafts(analysis);
  if (!read) {
    return [];
  }

  const segments = (
    analysis.structuredData?.audience as { corePersonas?: AudienceSegment[] } | undefined
  )?.corePersonas;

  return read.drafts.items.map((item) => ({
    draftId: item.draftId,
    envelope: read.envelope,
    status: item.status,
    saved: Boolean(item.savedPersonaId),
    segment: segments?.[item.sourceIndex] ?? {},
    persona: item.persona,
    savedPersonaId: item.savedPersonaId,
  }));
};
